import * as Sentry from '@sentry/react'

import FluxComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import { selectEditorForDraft } from '../editorSlice'
import type { ComposerStore as ZustandComposerStore } from '../composerStore'

// Coarse buckets for `sessionAgeMs`, so the Sentry issue's tag-distribution
// view shows how fresh a session was at drift time without opening every
// event — matches `channelSelectionDrift.ts`'s bucketing exactly.
const bucketSessionAge = (ms: number): string => {
  if (ms < 1_000) return '<1s'
  if (ms < 10_000) return '<10s'
  if (ms < 60_000) return '<1min'
  if (ms < 600_000) return '<10min'
  return '10min+'
}

/**
 * Compares every Flux draft's `editorState` reference against Zustand's
 * `editorSlice.editors` mirror, in both directions — a draft whose editor
 * isn't mirrored, and an `editorSlice` entry whose draft no longer exists in
 * Flux. Called at the end of `installFluxToEditorStateMirror`'s every pass —
 * same "parity check runs right after the mirror settles" shape as
 * `checkOmniParityAfterMirror` — so a mismatch here means the mirror's own
 * write or removal loop missed something, not that mirroring hasn't happened
 * yet.
 *
 * Unlike the content tracks' deep-equal checks, this is a reference-identity
 * comparison: the mirrored thing is a live object pointer, not a value (see
 * `~/notes/notes/claude/plans/composer/editor-state-migration.md`).
 */
export function checkEditorStateParity(
  store: ZustandComposerStore,
  sessionAgeMs = 0,
): void {
  const isProduction = process.env.NODE_ENV === 'production'
  // 0.04 local sample x 0.25 Sentry client sampleRate (PublishSentryClient
  // applies it to every captured event) = ~1% of drift occurrences reported.
  // Keep in sync with the client rate, and with the other bridge checks.
  if (isProduction && Math.random() > 0.04) return

  const drafts = FluxComposerStore.getDrafts()
  const state = store.getState()
  const fluxDraftIds = new Set<string>(drafts.map((draft) => draft.id))

  const missingFromZustand = drafts
    .filter((draft) => draft.editorState)
    .filter(
      (draft) => selectEditorForDraft(state, draft.id) !== draft.editorState,
    )
    .map((draft) => draft.id)

  // The mirror's removal branch has no live trigger from normal composer use
  // (see `installFluxToEditorStateMirror.ts`) but does fire around
  // `resetState()` — check both directions so a stale entry the removal
  // loop should have dropped is actually observable here, not just assumed
  // correct because the forward direction agrees.
  const orphanedInZustand = Object.keys(state.editors).filter(
    (draftId) => !fluxDraftIds.has(draftId),
  )

  const mismatchedDraftIds = [
    ...new Set([...missingFromZustand, ...orphanedInZustand]),
  ]

  if (mismatchedDraftIds.length === 0) return

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error(
      '[composer.bridge] editorState drift detected',
      `draftIds=${mismatchedDraftIds.join(',')}`,
    )
    return
  }

  Sentry.captureEvent({
    message: 'composer.bridge.drift',
    level: 'warning',
    fingerprint: ['composer-bridge-drift', 'editorState'],
    tags: {
      track: 'editorState',
      sessionAgeBucket: bucketSessionAge(sessionAgeMs),
    },
    extra: {
      draftIds: mismatchedDraftIds.join(','),
      sessionAgeMs,
    },
  })
  Sentry.addBreadcrumb({
    category: 'composer.bridge',
    level: 'warning',
    message: 'editorState drift',
    data: {
      track: 'editorState',
      draftIds: mismatchedDraftIds,
      sessionAgeMs,
    },
  })
}
